import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { BAD_EMAIL, REQUIRED, SUBMIT_FAIL, apiFieldErrors, blank, scrollToFirstError, submitErrorMessage } from '@/lib/formErrors';

import { SubmittedDialog } from '@/components/SubmittedDialog';
import { ComboBox } from '@/components/ComboBox';
import { useToast } from '@/components/Toast';
import {
  useCreateClient, useLookup, useClientSearch, type ClientPickerRow,
} from '@/api/hooks';
import { api } from '@/api/client';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const US_CA = ['United States', 'Canada'];
// State/province lists per country (verbatim from Power Apps ComboBox_State).
const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const EIN1_RE = /^\d{3}-\d{2}-\d{4}$|^\d{2}-\d{7}$/;
const EIN2_RE = /^\d{3}-\d{2}-\d{4}$/;

type Form = Record<string, string> & { surveyOptIn?: boolean };

export function NewClient() {
  const navigate = useNavigate();
  const notify = useToast();
  const create = useCreateClient();
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmKind, setConfirmKind] = useState<'submit' | 'duplicate' | null>(null);
  // Item 19: thank-you pop-up after a successful submit (replaces the success toast).
  const [submitted, setSubmitted] = useState<'submit' | 'duplicate' | null>(null);

  // Identity + billing cascade (controlled, mirrors Power Apps ClientOnboarding).
  const [clientType, setClientType] = useState('');            // '' | 'Master' | 'New Sub'
  const [masterQ, setMasterQ] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientPickerRow | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [apiErrs, setApiErrs] = useState<Record<string, string>>({});
  const [billOption, setBillOption] = useState('');            // '' | 'Self' | 'Master' | 'Other'
  const [billingQ, setBillingQ] = useState('');
  const [selectedBilling, setSelectedBilling] = useState<ClientPickerRow | null>(null);
  const [survey, setSurvey] = useState(false);                 // Checkbox_Survey → varSurvey

  const { register, handleSubmit, watch, getValues, setValue } = useForm<Form>({
    defaultValues: { clientSubId: '0' },
  });

  const managers = useLookup('client-managers');
  const industries = useLookup('brc-industries');
  const naics = useLookup('naics');
  const entityTypes = useLookup('entity-types');
  const countries = useLookup('countries');

  // Item 15: type-to-search pickers (the "starts with" letter dropdowns are gone).
  const masterPicker = useClientSearch(clientType === 'New Sub' ? masterQ : '', false);
  const billingPicker = useClientSearch(billOption === 'Other' ? billingQ : '', true);

  const billOptions = clientType === 'Master' ? ['Self', 'Other'] : ['Self', 'Master', 'Other'];

  const w = watch();
  const country = w.country ?? '';
  const needsState = US_CA.includes(country);
  const stateItems = country === 'United States' ? US_STATES : country === 'Canada' ? CA_PROVINCES : [];

  // View-only Engagement Location, derived from the selected Client Manager
  // (Power Apps: LookUp(vw_EmployeeEngLocation, EmployeeName = ClientManager)).
  const engLocation = useQuery({
    queryKey: ['eng-location', w.clientManager ?? ''],
    queryFn: () => api.get<{ EngLocation: string }[]>(`/lookups/engagement-locations?partner=${encodeURIComponent(w.clientManager ?? '')}`),
    enabled: !!w.clientManager,
  });

  // Format validity (empty is allowed except where required) — Power Apps "!" checks.
  const clientEmailBad = !blank(w.clientEmail) && !EMAIL_RE.test(String(w.clientEmail).trim());
  const secEmailBad = !blank(w.secondaryEmail) && !EMAIL_RE.test(String(w.secondaryEmail).trim());
  const surveyEmailBad = !blank(w.surveyEmail) && !EMAIL_RE.test(String(w.surveyEmail).trim());
  const ein1Bad = !!w.taxSSN1 && !EIN1_RE.test(w.taxSSN1);
  const ein2Bad = !!w.taxSSN2 && !EIN2_RE.test(w.taxSSN2);
  const billStmtEmailBad = !blank(w.billingStatementEmail) && !EMAIL_RE.test(String(w.billingStatementEmail).trim());

  function changeType(t: string) {
    setClientType(t);
    setSelectedClient(null);
    setBillOption(''); setSelectedBilling(null);
  }

  function changeCountry(v: string) {
    setValue('country', v);
    setValue('state', ''); // Country OnChange → Reset(ComboBox_State)
  }


  // Item: highlight the fields blocking a submit (shown after the first Submit attempt).
  const errs: Record<string, string> = {};
  {
    const v = w as Record<string, string | undefined>;
    for (const k of ['clientName', 'contactName', 'mailingAddress1', 'city', 'zipCode', 'phone', 'clientEmail', 'billingStatementEmail']) {
      if (blank(v[k])) errs[k] = REQUIRED;
    }
    for (const k of ['clientManager', 'entityType', 'brcIndustry', 'naics', 'fiscalYearEndMonth']) if (blank(v[k])) errs[k] = REQUIRED;
    if (blank(country)) errs.country = REQUIRED;
    if (needsState && blank(v.state)) errs.state = REQUIRED;
    if (!errs.clientEmail && clientEmailBad) errs.clientEmail = BAD_EMAIL;
    if (secEmailBad) errs.secondaryEmail = BAD_EMAIL;
    if (surveyEmailBad) errs.surveyEmail = BAD_EMAIL;
    if (!errs.billingStatementEmail && billStmtEmailBad) errs.billingStatementEmail = BAD_EMAIL;
    if (ein1Bad) errs.taxSSN1 = 'Format: XX-XXXXXXX or XXX-XX-XXXX';
    if (ein2Bad) errs.taxSSN2 = 'Format: XXX-XX-XXXX';
    if (!clientType) errs.clientType = REQUIRED;
    if (clientType === 'New Sub' && !selectedClient) errs.masterClient = REQUIRED;
    if (!billOption) errs.billTo = REQUIRED;
    if (billOption === 'Other' && !selectedBilling) errs.billingClient = REQUIRED;
  }
  const E: Record<string, string | undefined> = showErrors ? { ...apiErrs, ...errs } : apiErrs;

  // Submit gate — mirrors Power Apps Button_Submit DisplayMode (incl. format checks).
  const invalid =
    !clientType ||
    (clientType === 'New Sub' && !selectedClient) ||
    !billOption ||
    (billOption === 'Other' && !selectedBilling) ||
    !w.clientName?.trim() || !w.contactName?.trim() || !w.mailingAddress1?.trim() ||
    !w.city?.trim() || !country || (needsState && !w.state?.trim()) ||
    !w.zipCode?.trim() || !w.phone?.trim() || !w.clientEmail?.trim() ||
    !w.clientManager || !w.entityType || !w.brcIndustry || !w.naics || !w.fiscalYearEndMonth ||
    !w.billingStatementEmail?.trim() ||
    clientEmailBad || secEmailBad || surveyEmailBad || ein1Bad || ein2Bad || billStmtEmailBad;

  async function submit(duplicate: boolean) {
    const v = getValues();
    const payload = {
      ...v,
      clientType,
      clientId: clientType === 'New Sub' ? (selectedClient?.ClientID ?? '') : '',
      clientSubId: '0',
      billingClientType: billOption,
      billingClientId: billOption === 'Other' ? (selectedBilling?.ClientID ?? '') : '',
    };
    try {
      await create.mutateAsync(payload);
      setConfirmKind(null);
      setSubmitted(duplicate ? 'duplicate' : 'submit');
    } catch (e) {
      setApiErrs(apiFieldErrors(e));
      setShowErrors(true);
      notify('error', submitErrorMessage(e));
      setConfirmKind(null);
      scrollToFirstError();
    }
  }

  /** Submit / Submit & Duplicate: highlight what's missing instead of a silent grey button. */
  function attempt(kind: 'submit' | 'duplicate') {
    setApiErrs({});
    if (invalid || Object.keys(errs).length) {
      setShowErrors(true);
      notify('error', SUBMIT_FAIL);
      scrollToFirstError();
      return;
    }
    setConfirmKind(kind);
  }

  if (!acknowledged) {
    return (
      <ConfirmDialog
        open
        message="I acknowledge that client acceptance procedures, including the conflict check, have been completed and stored in the appropriate location."
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirm={() => setAcknowledged(true)}
        onCancel={() => navigate('/')}
      />
    );
  }

  return (
    <form
      className="w-full space-y-4 p-5"
      onSubmit={handleSubmit(() => attempt('submit'))}
    >
      <div className="flex justify-end">
        <p className="text-right text-sm font-semibold text-[#3256a0]">
          Note: Client Acceptance must be complete prior to submitting this form.
        </p>
      </div>

      <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
      <Section title="Client Information">
        <Field error={E.clientType} label="Type of Client *">
          <select className="input" value={clientType} onChange={(e) => changeType(e.target.value)}>
            <option value="">— select —</option>
            <option value="Master">New Master Client</option>
            <option value="New Sub">New Sub of Existing Client</option>
          </select>
        </Field>

        <Field error={E.masterClient} label={`Master Client ID ${clientType === 'New Sub' ? '*' : ''}`}>
          <ComboBox<ClientPickerRow>
            items={masterPicker.data ?? []}
            value={selectedClient}
            onChange={setSelectedClient}
            getLabel={(c) => c.DisplayText ?? `${c.ClientID} - ${c.ClientName}`}
            getKey={(c) => c.ClientID}
            placeholder="Type to search by ID or name…"
            disabled={clientType !== 'New Sub'}
            loading={masterPicker.isFetching}
            onQueryChange={setMasterQ}
          />
        </Field>

        <Field error={E.clientName} label="Client Name *">
          <input className="input" {...register('clientName')} />
          <p className="mt-1 text-xs text-muted-foreground">If client is an individual, use the &quot;Last Name, First Name&quot; format.</p>
        </Field>
        <Field error={E.clientEmail} label={<>Client Email * <Warn show={clientEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" {...register('clientEmail')} />
        </Field>
        <Field error={E.secondaryEmail} label={<>Secondary Email <Warn show={secEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" {...register('secondaryEmail')} />
        </Field>

        <Field error={E.billTo} label="Bill To *">
          <select className="input" value={billOption}
            onChange={(e) => { setBillOption(e.target.value); setSelectedBilling(null); }}>
            <option value="">— select —</option>
            {billOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>

        <Field error={E.billingClient} label={`Billing Client ID ${billOption === 'Other' ? '*' : ''}`}>
          <ComboBox<ClientPickerRow>
            items={billingPicker.data ?? []}
            value={selectedBilling}
            onChange={setSelectedBilling}
            getLabel={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
            getKey={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
            placeholder="Type to search by ID or name…"
            disabled={billOption !== 'Other'}
            loading={billingPicker.isFetching}
            onQueryChange={setBillingQ}
          />
        </Field>

        <Field error={E.billingStatementEmail} label={<>Billing Statement Email * <Warn show={billStmtEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" {...register('billingStatementEmail')} />
        </Field>
      </Section>

      <Section title="Contact Details">
        <Field error={E.contactName} label="Contact Name *"><input className="input" {...register('contactName')} /></Field>
        <Field error={E.mailingAddress1} label="Mailing Address 1 *"><input className="input" {...register('mailingAddress1')} /></Field>
        <Field label="Mailing Address 2"><input className="input" {...register('mailingAddress2')} /></Field>
        <Field error={E.city} label="City *"><input className="input" {...register('city')} /></Field>
        <Field error={E.country} label="Country *">
          <LookupCombo data={countries.data} field="Value" value={country}
            onChange={changeCountry} placeholder="Select country…" />
        </Field>
        <Field error={E.state} label={`State/Province ${needsState ? '*' : ''}`}>
          <ComboBox<string>
            items={stateItems}
            value={w.state ? w.state : null}
            onChange={(s) => setValue('state', s ?? '')}
            getLabel={(s) => s}
            getKey={(s) => s}
            placeholder="Select state…"
            disabled={!needsState}
          />
        </Field>
        <Field error={E.zipCode} label="Zip/Postal Code *"><input className="input" {...register('zipCode')} /></Field>
        <Field error={E.phone} label="Phone *"><input className="input" {...register('phone')} /></Field>
        <Field label="Secondary Phone"><input className="input" {...register('secondaryPhone')} /></Field>
      </Section>

      <Section title="Other Details">
        <Field error={E.clientManager} label="Client Manager *">
          <LookupCombo data={managers.data} field="ClientManager" value={w.clientManager ?? ''}
            onChange={(v) => setValue('clientManager', v)} placeholder="Select manager…" />
        </Field>
        <Field label="Engagement Location">
          <input className="input" value={String(engLocation.data?.[0]?.EngLocation ?? '')} disabled placeholder="Set by Client Manager" />
        </Field>
        <Field error={E.entityType} label="Entity Type *">
          <LookupCombo data={entityTypes.data} field="EntityType" labelField="Description" value={w.entityType ?? ''}
            onChange={(v) => setValue('entityType', v)} placeholder="Select entity type…" />
        </Field>
        <Field error={E.brcIndustry} label="BRC Industry *">
          <LookupCombo data={industries.data} field="Industry" value={w.brcIndustry ?? ''}
            onChange={(v) => setValue('brcIndustry', v)} placeholder="Select industry…" />
        </Field>
        <Field error={E.naics} label="NAICS *">
          <LookupCombo data={naics.data} field="NAICS" value={w.naics ?? ''}
            onChange={(v) => setValue('naics', v)} placeholder="Search NAICS…" />
        </Field>
        <Field error={E.fiscalYearEndMonth} label="Fiscal Year End Month *">
          <select className="input" {...register('fiscalYearEndMonth')}>
            <option value="">— select —</option>
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field error={E.taxSSN1} label={<>Tax EIN/Taxpayer SSN <Warn show={ein1Bad} tip="Format: XX-XXXXXXX or XXX-XX-XXXX" /></>}>
          <input className="input" maxLength={11} placeholder="00-0000000" {...register('taxSSN1')} />
        </Field>
        <Field error={E.taxSSN2} label={<>Spouse SSN <Warn show={ein2Bad} tip="Format: XXX-XX-XXXX" /></>}>
          <input className="input" maxLength={11} placeholder="000-00-0000" {...register('taxSSN2')} />
        </Field>
        <Field label="Referral Source"><input className="input" {...register('referralSource')} /></Field>
      </Section>

      <Section title="Survey">
        <div className="flex items-center gap-2 col-span-full">
          <input
            id="surveyChk"
            type="checkbox"
            checked={survey}
            onChange={(e) => {
              setSurvey(e.target.checked);
              if (!e.target.checked) { setValue('surveyEmail', ''); setValue('surveyAttention', ''); }
            }}
          />
          <label htmlFor="surveyChk" className="text-sm font-semibold text-foreground">Survey - Check if different from contact above</label>
        </div>
        <Field error={E.surveyEmail} label={<>Survey Email <Warn show={surveyEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" disabled={!survey} {...register('surveyEmail')} />
        </Field>
        <Field label="Survey Attention">
          <input className="input" disabled={!survey} {...register('surveyAttention')} />
        </Field>
      </Section>
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input" rows={3} {...register('note')} />
      </div>

      <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

      <div className="flex gap-3">
        <button type="submit" className="btn-success" disabled={create.isPending}>Submit</button>
        <button type="button" className="btn-secondary" disabled={create.isPending} onClick={() => attempt('duplicate')}>
          Submit &amp; Duplicate
        </button>
      </div>

      <SubmittedDialog
        open={submitted !== null}
        onClose={() => {
          // Submit goes back to the list; Submit & Duplicate stays on the form for the next client.
          if (submitted === 'submit') navigate('/clients');
          setSubmitted(null);
        }}
      />
      <ConfirmDialog
        open={confirmKind !== null}
        message="Do you wish to submit the client form? Press 'Yes' to continue, 'No' to cancel."
        onConfirm={() => submit(confirmKind === 'duplicate')}
        onCancel={() => setConfirmKind(null)}
      />
    </form>
  );
}

/** ComboBox bound to a lookup table by a single display column (stores the string). */
function LookupCombo({ data, field, labelField, value, onChange, placeholder, disabled }: {
  data?: Record<string, unknown>[];
  field: string;
  labelField?: string; // shown in the list when it differs from the saved field (Entity Type)
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const rows = Array.isArray(data) ? data : [];
  const selected = rows.find((r) => String(r[field] ?? '') === value) ?? null;
  return (
    <ComboBox
      items={rows}
      value={selected}
      onChange={(r) => onChange(r ? String(r[field] ?? '') : '')}
      getLabel={(r) => String(r[labelField ?? field] ?? '')}
      getKey={(r) => String(r[field] ?? '')}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function Warn({ show, tip }: { show: boolean; tip: string }) {
  if (!show) return null;
  return <span title={tip} className="ml-1 font-bold text-orange-500">!</span>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 className="mb-2 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-foreground">{title}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}
function Field({ label, children, error }: { label: React.ReactNode; children: React.ReactNode; error?: string }) {
  // error: red border on the input + message underneath (fields blocking a submit).
  return (
    <div data-field-error={error ? 'true' : undefined} className={error ? 'field-error' : undefined}>
      <label className="label">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs font-medium text-destructive">{error}</p>}
    </div>
  );
}
